/**
 * Generic Audit Trail Helper
 *
 * Provides a unified interface for logging changes with before/after state
 * across all modules. Implements the "Undo and Audit Trail from Day One"
 * decision from the Forge Decisions Log.
 *
 * Usage:
 *   import { logAudit, revertAuditEntry } from "@/lib/audit";
 *
 *   // Log a change
 *   await logAudit({
 *     tableName: "people",
 *     recordId: person.id,
 *     action: "update",
 *     organizationId: org.id,
 *     oldData: previousPerson,
 *     newData: updatedPerson,
 *   });
 *
 *   // Revert a change (uses old_data from the audit entry)
 *   await revertAuditEntry(auditEntryId);
 */

import { supabase } from "@/integrations/supabase/client";

// ---------------------------------------------------------------------------
// Types
// ---------------------------------------------------------------------------

export interface AuditLogParams {
  /** The DB table that was modified */
  tableName: string;
  /** Primary key of the affected record */
  recordId: string;
  /** Human-readable action: create, update, delete, restore, revert, etc. */
  action: string;
  /** Organization context */
  organizationId: string;
  /** Actor — defaults to current auth user if omitted */
  userId?: string | null;
  /** State before the change (null for creates) */
  oldData?: Record<string, unknown> | null;
  /** State after the change (null for deletes) */
  newData?: Record<string, unknown> | null;
  /** Optional freeform metadata (reason, source, batch id, etc.) */
  metadata?: Record<string, unknown> | null;
}

export interface AuditLogEntry {
  id: string;
  table_name: string;
  record_id: string;
  action: string;
  old_data: Record<string, unknown> | null;
  new_data: Record<string, unknown> | null;
  user_id: string | null;
  created_at: string;
  organization_id: string | null;
  metadata?: Record<string, unknown> | null;
}

// ---------------------------------------------------------------------------
// Core: Write an audit log entry
// ---------------------------------------------------------------------------

export async function logAudit({
  tableName,
  recordId,
  action,
  organizationId,
  userId,
  oldData = null,
  newData = null,
  metadata = null,
}: AuditLogParams): Promise<{ id: string } | null> {
  // Write via the log_audit_event SECURITY DEFINER RPC. audit_logs RLS has no
  // INSERT policy, so a direct client insert is denied (42501) and silently
  // fails; the RPC self-authorizes the caller as a member of organizationId and
  // sets user_id = auth.uid() server-side (the userId arg can no longer spoof
  // the actor — by design). Non-throwing: returns null on any failure, as before.
  const { data, error } = await supabase.rpc("log_audit_event", {
    p_table_name: tableName,
    p_record_id: recordId,
    p_action: action,
    p_organization_id: organizationId,
    p_old_data: (oldData ?? null) as never,
    p_new_data: (newData ?? null) as never,
  });

  if (error) {
    console.error(`[audit] Failed to log ${action} on ${tableName}/${recordId}`, error);
    return null;
  }

  return data ? { id: data as string } : null;
}

// ---------------------------------------------------------------------------
// Convenience: log with automatic before/after diff
// ---------------------------------------------------------------------------

/**
 * Captures the current row state, executes the mutation, then logs both
 * states to the audit trail. Returns the mutation result.
 */
export async function withAuditTrail<T>(
  params: {
    tableName: string;
    recordId: string;
    action: string;
    organizationId: string;
    selectColumns?: string;
  },
  mutation: () => Promise<T>
): Promise<T> {
  const { tableName, recordId, action, organizationId, selectColumns = "*" } = params;

  // Capture before state
  const { data: beforeRow } = await supabase
    .from(tableName as any)
    .select(selectColumns)
    .eq("id", recordId)
    .single();

  // Run the mutation
  const result = await mutation();

  // Capture after state
  const { data: afterRow } = await supabase
    .from(tableName as any)
    .select(selectColumns)
    .eq("id", recordId)
    .single();

  // Write the audit entry (fire-and-forget)
  logAudit({
    tableName,
    recordId,
    action,
    organizationId,
    oldData: beforeRow as Record<string, unknown> | null,
    newData: afterRow as Record<string, unknown> | null,
  });

  return result;
}

// ---------------------------------------------------------------------------
// Revert: restore a record to its previous state
// ---------------------------------------------------------------------------

/**
 * Reverts a record to the state captured in an audit log entry's `old_data`.
 *
 * - Fetches the audit entry
 * - Validates old_data exists
 * - Updates the target record with old_data values
 * - Logs a new "revert" audit entry
 *
 * Returns the new audit entry ID or throws on failure.
 */
export async function revertAuditEntry(
  auditEntryId: string
): Promise<{ revertAuditId: string }> {
  // 1. Fetch the audit entry
  const { data: entry, error: fetchError } = await supabase
    .from("audit_logs")
    .select("*")
    .eq("id", auditEntryId)
    .single();

  if (fetchError || !entry) {
    throw new Error(`Audit entry ${auditEntryId} not found`);
  }

  const auditEntry = entry as unknown as AuditLogEntry;

  if (!auditEntry.old_data || Object.keys(auditEntry.old_data).length === 0) {
    throw new Error(
      `Cannot revert: audit entry ${auditEntryId} has no old_data (was this a create?)`
    );
  }

  // 2. Capture current state before revert
  const { data: currentRow } = await supabase
    .from(auditEntry.table_name as any)
    .select("*")
    .eq("id", auditEntry.record_id)
    .single();

  // 3. Build the revert payload — only include columns that existed in old_data
  // and exclude system columns that shouldn't be overwritten
  const SYSTEM_COLUMNS = new Set([
    "id",
    "created_at",
    "organization_id",
  ]);

  const revertPayload: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(auditEntry.old_data)) {
    if (!SYSTEM_COLUMNS.has(key)) {
      revertPayload[key] = value;
    }
  }

  // 4. Apply the revert
  const { error: updateError } = await supabase
    .from(auditEntry.table_name as any)
    .update(revertPayload as any)
    .eq("id", auditEntry.record_id);

  if (updateError) {
    throw new Error(
      `Failed to revert ${auditEntry.table_name}/${auditEntry.record_id}: ${updateError.message}`
    );
  }

  // 5. Log the revert as its own audit entry
  const revertLog = await logAudit({
    tableName: auditEntry.table_name,
    recordId: auditEntry.record_id,
    action: "revert",
    organizationId: auditEntry.organization_id ?? "",
    oldData: currentRow as Record<string, unknown> | null,
    newData: auditEntry.old_data,
    metadata: { reverted_audit_entry: auditEntryId },
  });

  return { revertAuditId: revertLog?.id ?? "" };
}

// ---------------------------------------------------------------------------
// Soft-delete recovery: restore soft-deleted records within 30 days
// ---------------------------------------------------------------------------

/**
 * Restores a soft-deleted record (is_deleted = true) by setting
 * is_deleted = false and deleted_at = null. Logs the restore action.
 *
 * The 30-day window is enforced: records deleted more than 30 days ago
 * cannot be restored.
 */
export async function restoreSoftDeleted(params: {
  tableName: string;
  recordId: string;
  organizationId: string;
}): Promise<void> {
  const { tableName, recordId, organizationId } = params;

  // Fetch the deleted record
  const { data: record, error } = await supabase
    .from(tableName as any)
    .select("*")
    .eq("id", recordId)
    .single();

  if (error || !record) {
    throw new Error(`Record ${tableName}/${recordId} not found`);
  }

  const rec = record as Record<string, any>;

  if (!rec.is_deleted) {
    throw new Error(`Record ${tableName}/${recordId} is not deleted`);
  }

  // Enforce 30-day window
  if (rec.deleted_at) {
    const deletedAt = new Date(rec.deleted_at);
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(thirtyDaysAgo.getDate() - 30);

    if (deletedAt < thirtyDaysAgo) {
      throw new Error(
        `Record ${tableName}/${recordId} was deleted more than 30 days ago and cannot be restored`
      );
    }
  }

  // Restore
  const { error: updateError } = await supabase
    .from(tableName as any)
    .update({ is_deleted: false, deleted_at: null } as any)
    .eq("id", recordId);

  if (updateError) {
    throw new Error(`Failed to restore: ${updateError.message}`);
  }

  // Audit the restore
  await logAudit({
    tableName,
    recordId,
    action: "restore",
    organizationId,
    oldData: { is_deleted: true, deleted_at: rec.deleted_at },
    newData: { is_deleted: false, deleted_at: null },
  });
}
